'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface FAQSectionProps {
  className?: string;
}

const FAQSection = ({ className = '' }: FAQSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      id: '1',
      question: 'Hoe snel kan ik een reactie verwachten?',
      answer: 'We streven ernaar om binnen 2 uur te reageren op alle aanvragen tijdens kantooruren (Ma-Vr 08:00-18:00). Voor urgente technische ondersteuning zijn we 24/7 bereikbaar voor bestaande klanten.',
      category: 'algemeen',
    },
    {
      id: '2',
      question: 'Wat gebeurt er na het indienen van mijn aanvraag?',
      answer: 'Na het indienen ontvangt u direct een bevestigingsmail. Een van onze experts neemt binnen 2 uur contact met u op om uw behoeften te bespreken en een vervolgafspraak in te plannen.',
      category: 'algemeen',
    },
    {
      id: '3',
      question: 'Kan ik een demo boeken zonder verplichtingen?',
      answer: 'Absoluut! Onze demo\'s zijn volledig vrijblijvend. We laten u zien hoe onze AI-oplossingen specifiek voor uw bedrijf kunnen werken, zonder enige verplichting.',
      category: 'demo',
    },
    {
      id: '4',
      question: 'Welke informatie moet ik voorbereiden voor een consultatie?',
      answer: 'Het helpt als u informatie heeft over uw huidige processen, uitdagingen, en doelen. Maar maak u geen zorgen - onze consultants begeleiden u door het hele proces en stellen de juiste vragen.',
      category: 'consultatie',
    },
    {
      id: '5',
      question: 'Zijn jullie kantoren toegankelijk voor rolstoelgebruikers?',
      answer: 'Ja, al onze kantoren in Amsterdam, Rotterdam en Brussel zijn volledig toegankelijk voor rolstoelgebruikers. Laat het ons weten bij het plannen van uw bezoek als u specifieke voorzieningen nodig heeft.',
      category: 'kantoor',
    },
    {
      id: '6',
      question: 'Hoe wordt mijn privacy beschermd?',
      answer: 'We nemen privacy zeer serieus en zijn volledig AVG/GDPR-compliant. Uw gegevens worden veilig opgeslagen en alleen gebruikt voor het doel waarvoor u ze heeft verstrekt. Lees onze privacyverklaring voor meer details.',
      category: 'privacy',
    },
  ];

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  if (!isHydrated) {
    return (
      <section className={`py-16 lg:py-24 bg-card ${className}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
              <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
              <div className="space-y-3 mt-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 lg:py-24 bg-card ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Veelgestelde vragen
            </h2>
            <p className="text-lg text-muted-foreground">
              Vind snel antwoorden op de meest gestelde vragen over contact opnemen.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="glassmorphic rounded-xl overflow-hidden transition-all duration-300 hover:glow-primary"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors duration-300"
                >
                  <span className="text-lg font-semibold text-foreground pr-4">{faq.question}</span>
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                      openId === faq.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openId === faq.id ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-4 text-muted-foreground">{faq.answer}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Staat uw vraag er niet bij?</p>
            <a
              href="/faq"
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all duration-300 hover:shadow-premium"
            >
              <span>Bekijk alle FAQ&apos;s</span>
              <Icon name="ArrowRightIcon" size={20} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;